import React from 'react';
import { Helmet } from 'react-helmet-async';
interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  twitterCard?: string;
  robots?: string;
  hreflangAr?: string;
  hreflangEn?: string;
  hreflangDefault?: string;
  prevUrl?: string;
  nextUrl?: string;
  ogPrice?: number;
  productPrice?: number;
  productCurrency?: string;
  author?: string;
  publisher?: string;
  keywords?: string;
  jsonLd?: object | object[];
}
export function SEO({
  title,
  description,
  canonical,
  ogType = 'website',
  ogImage,
  ogPrice,
  productPrice,
  productCurrency = 'SAR',
  robots = 'index,follow',
  twitterCard = 'summary_large_image',
  hreflangAr,
  hreflangEn,
  hreflangDefault,
  prevUrl,
  nextUrl,
  author,
  keywords,
  jsonLd
}: SEOProps) {
  const fullTitle = `${title} | Innova Pharmacy`;
  const fullCanonical = canonical ? `https://www.innovaksa.net${canonical}` : undefined;
  return <Helmet data-id="element-5645" data-mp="3pkxuc3i230ny">
      {/* Resource Hints for Performance */}
      <link rel="preconnect" href="https://img.innovapharmacy.com" data-id="element-5646" data-mp="1tcuxnnddpul8" />
      <link rel="preconnect" href="https://img-test.innovaksa.net" data-id="element-5647" data-mp="1of1pwpwfgsiu" />
      <link rel="preconnect" href="https://img.innovapharmacy.com" data-id="element-5648" data-mp="1ocpbgmpvp0aj" />
      <link rel="preconnect" href="https://fonts.googleapis.com" crossOrigin="anonymous" data-id="element-5649" data-mp="224ww5g3w10sv" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" data-id="element-5650" data-mp="1y5ejf7m7zw14" />
      {/* Basic Meta Tags */}
      <title data-id="element-5651" data-mp="15gh1j5lx818w">{fullTitle}</title>
      <meta name="description" content={description} data-id="element-5652" data-mp="15h8x4gfipyz6" />
      <meta name="author" content={author || 'Innova Pharmacy'} data-id="element-5653" data-mp="1jfgugpggv2av" />
      <meta name="publisher" content="Innova Pharmacy" data-id="element-5654" data-mp="1jdm58yy5wo0z" />
      {keywords && <meta name="keywords" content={keywords} data-id="element-5655" data-mp="1efufobkzpdzy" />}
      {fullCanonical && <link rel="canonical" href={fullCanonical} data-id="element-5656" data-mp="2uucgwxlcu81z" />}
      <meta name="robots" content={robots} data-id="element-5657" data-mp="2ut13s760rny7" />
      {/* Hreflang Tags */}
      {hreflangAr && <link rel="alternate" hreflang="ar-SA" href={`https://www.innovaksa.net${hreflangAr}`} data-id="element-5658" data-mp="2pvj477dw6l2v" />}
      {hreflangEn && <link rel="alternate" hreflang="en-SA" href={`https://www.innovaksa.net${hreflangEn}`} data-id="element-5659" data-mp="1sgemquro1fc1" />}
      {hreflangDefault && <link rel="alternate" hreflang="x-default" href={`https://www.innovaksa.net${hreflangDefault}`} data-id="element-5660" data-mp="21hvxryq7uqtv" />}
      {/* Pagination Links */}
      {prevUrl && <link rel="prev" href={`https://www.innovaksa.net${prevUrl}`} data-id="element-5661" data-mp="18syt4yexgsjl" />}
      {nextUrl && <link rel="next" href={`https://www.innovaksa.net${nextUrl}`} data-id="element-5662" data-mp="1ds1o1q171idu" />}
      {/* Open Graph Tags */}
      <meta property="og:type" content={ogType} data-id="element-5663" data-mp="1ijjnuarhdcx8" />
      <meta property="og:site_name" content="Innova Pharmacy" data-id="element-5664" data-mp="1ilcw1uhqs57c" />
      <meta property="og:title" content={fullTitle} data-id="element-5665" data-mp="l2umsjacueow" />
      <meta property="og:description" content={description} data-id="element-5666" data-mp="q2z8qou181mi" />
      {fullCanonical && <meta property="og:url" content={fullCanonical} data-id="element-5667" data-mp="q0w6n6flui1f" />}
      {ogImage && <meta property="og:image" content={ogImage} data-id="element-5668" data-mp="ii557734im04" />}
      <meta property="og:locale" content="en_US" data-id="element-5669" data-mp="dkd9s339shr6" />
      <meta property="og:locale:alternate" content="ar_SA" data-id="element-5670" data-mp="dkwjiwirnzu4" />
      {ogPrice && <meta property="og:price:standard_amount" content={ogPrice.toString()} data-id="element-5671" data-mp="8k8w0ktqy14n" />}
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content={twitterCard} data-id="element-5672" data-mp="4m1c5h3zkjp8" />
      <meta name="twitter:title" content={fullTitle} data-id="element-5673" data-mp="3vqrpa6h6szro" />
      <meta name="twitter:description" content={description} data-id="element-5674" data-mp="3qz9opjj2t178" />
      {ogImage && <meta name="twitter:image" content={ogImage} data-id="element-5675" data-mp="3qx6n028k9jmo" />}
      {/* Product Price Tags */}
      {productPrice && <>
          <meta property="product:price:amount" content={productPrice.toString()} data-id="element-5676" data-mp="3h1deqv1vkcap" />
          <meta property="product:price:currency" content={productCurrency} data-id="element-5677" data-mp="3d00agpefkh5w" />
        </>}
      {/* JSON-LD Structured Data */}
      {jsonLd && <script type="application/ld+json" data-id="element-5678" data-mp="382i6zej6mu5a">
          {JSON.stringify(Array.isArray(jsonLd) ? jsonLd : [jsonLd])}
        </script>}
    </Helmet>;
}